import type { PfPatientFormRecord } from '@elva/ui';
import type { RequestOptions } from '@/api/client';
import { formsApi } from '@/features/forms/api/formsApi';
import { staffPatientFormsApi } from '@/features/forms/api/publicFormsApi';
import type { FormFillSession, FormSubmissionDetail } from '@/features/forms/api/types';
import { adaptSubmissionToSession, isStaffSubmissionEditable, normalizeSubmissionDetail } from '../adapters/formSubmissionAdapters';
import { buildResolvedFormFromTemplate } from '../adapters/resolvedFormFromTemplate';
import { logPatientWire } from '../debug/logPatientWire';

function pickFirstId(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function extractTokenFromPatientUrl(url: string | undefined): string | undefined {
  if (!url?.trim()) return undefined;
  const patterns = [
    /\/patient\/forms\/([^/?#]+)/i,
    /\/forms\/patient\/([^/?#]+)/i,
    /\/public\/forms\/([^/?#]+)/i,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]?.trim()) return match[1].trim();
  }
  return undefined;
}

export async function resolvePatientFormToken(
  form: PfPatientFormRecord,
  assignmentIdOverride?: string,
): Promise<string | undefined> {
  const direct = pickFirstId(form.accessToken);
  if (direct) return direct;

  const assignmentId = pickFirstId(assignmentIdOverride, form.assignmentId);
  if (!assignmentId) return undefined;

  try {
    const link = await formsApi.mintPatientLink(assignmentId);
    logPatientWire('minted patient form link', { assignmentId, link });
    const record = (link && typeof link === 'object' ? link : {}) as Record<string, unknown>;
    const nested =
      record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : record;
    const token = typeof nested.token === 'string' ? nested.token : undefined;
    const patientUrl = typeof nested.patient_url === 'string' ? nested.patient_url : undefined;
    return pickFirstId(token, extractTokenFromPatientUrl(patientUrl));
  } catch (error) {
    logPatientWire('mint patient form link failed', { assignmentId, error });
    return undefined;
  }
}

async function loadSessionFromSubmission(
  detail: FormSubmissionDetail,
  fallbackName?: string,
): Promise<FormFillSession | null> {
  const withResolved = adaptSubmissionToSession(detail, fallbackName);
  if (withResolved) return withResolved;

  const formId = pickFirstId(detail.form_id, detail.form?.id);
  if (!formId) return null;

  logPatientWire('building resolved_form from template', { formId });
  const resolvedForm = await buildResolvedFormFromTemplate(formId, detail.version ?? detail.form?.version);
  if (!resolvedForm) return null;

  return adaptSubmissionToSession({ ...detail, resolved_form: resolvedForm }, fallbackName);
}

/**
 * Staff read-only review.
 *
 * W23 / FD140 RP-3: the drawer reads the **staff-scoped** submission endpoint
 * (`GET /api/forms/submissions/{id}`), which works with the patient DOB gate
 * ON. The detail payload already carries `resolved_form`, so this renders
 * directly. The patient token-session route is a fallback only for a form with
 * no submission yet (nothing to review, but the staff may want a preview).
 */
export async function loadPatientFormReadSession(
  form: PfPatientFormRecord,
  options?: RequestOptions,
): Promise<FormFillSession> {
  logPatientWire('load patient form read session', { form });

  const submissionId = form.submissionId?.trim();

  if (submissionId) {
    const detail = await formsApi.getSubmission(submissionId, options);
    logPatientWire(`GET /api/forms/submissions/${submissionId}`, normalizeSubmissionDetail(detail));
    const session = await loadSessionFromSubmission(normalizeSubmissionDetail(detail), form.name);
    if (session) return session;
    logPatientWire('submission had no resolved_form/template; falling back to patient token session', null);
  }

  // The fallback mints a patient link (a write) — don't start it for a drawer that already closed.
  options?.signal?.throwIfAborted();
  const token = await resolvePatientFormToken(form);
  if (token) {
    logPatientWire(`GET /api/forms/patient/${token}`, null);
    const session = await staffPatientFormsApi.getSession(token, options);
    return {
      ...session,
      editable: isStaffSubmissionEditable(session.assignment.status),
    };
  }

  throw new Error('This form has no submission or patient access link yet.');
}
